import {DIALOG_CLOSED_EVENT, DIALOG_OPENED_EVENT, EopModalDialog} from "./modalDialog";
import {autoRegister, resolve} from "../../../container";
import {PageScrollbar} from "../../../common/pageScrollbar";
import {EventBus, EventBusEvent} from "../../../common/eventBus";
import {DialogActivationFactory} from "./dialogActivation";
import {PageFeatures} from "../pageFeatures";
import {Backdrop} from "../backdrop";
import {Dictionary} from "../dictionary";

export class DialogOpener {

    public constructor(
        private dialogElement: EopModalDialog,
        private dialogContent: HTMLElement,
        private pageScrollbar: PageScrollbar = resolve(PageScrollbar),
        protected pageFeatures: PageFeatures = resolve(PageFeatures),
        protected eventBus: EventBus = resolve(EventBus),
        private backdrop: Backdrop = resolve(Backdrop)
    ) {
        eventBus.on(DIALOG_CLOSED_EVENT, () => this.cleanUp());
        eventBus.on(DIALOG_OPENED_EVENT, event => this.closeOtherDialogs(event));
    }

    public openDialog(e: Event): void {
        if (e instanceof KeyboardEvent && e.key !== "Enter") {
            return;
        }
        if (this.dialogElement.classList.contains("inactive")
            || this.dialogContent.classList.contains("inactive")) {
            return;
        }

        this.pageScrollbar.disablePageScrollability();
        this.backdrop.show();
        void this.dialogElement.showModal();
    }

    private closeOtherDialogs(event: EventBusEvent): void {
        const dialogs = this.pageFeatures.find<EopModalDialog>(this.dialogElement.nodeName);
        const openingDialog = event.data as EopModalDialog;
        Array.from(dialogs ?? [])
            .filter(dialog => dialog !== openingDialog)
            .filter(dialog => dialog.isActive())
            .forEach(dialog => dialog.close());
    }

    private cleanUp(): void {
        if (this.noOpenDialog()) {
            this.backdrop.hide();
            this.pageScrollbar.enablePageScrollability();
        }
    }

    private noOpenDialog(): boolean {
        const dialogs = this.pageFeatures.find<EopModalDialog>(this.dialogElement.nodeName);
        return Array.from(dialogs ?? [])
            .filter(dialog => dialog.isActive())
            .isEmpty();
    }
}

@autoRegister()
export class DialogOpenerFactory {

    public constructor(
        private dialogActivationFactory: DialogActivationFactory = resolve(DialogActivationFactory),
        private pageFeatures: PageFeatures = resolve(PageFeatures)) {
    }

    public create(
        dialogContent: HTMLElement,
        disableCloseOnOutsideClick: boolean = false): DialogOpener {
        return this.dialogOpener(dialogContent, Dictionary.of(dialogContent), disableCloseOnOutsideClick);
    }

    public createWithAnchor(
        dialogContent: HTMLElement,
        anchorElement: HTMLElement,
        activeFromBreakpoint: string = "xs",
        disableCloseOnOutsideClick: boolean = false
    ): DialogOpener {
        this.dialogActivationFactory.create(anchorElement, dialogContent, activeFromBreakpoint);
        return this.dialogOpener(dialogContent, Dictionary.of(anchorElement), disableCloseOnOutsideClick);
    }

    private dialogOpener(
        dialogContent: HTMLElement,
        dictionary: Dictionary,
        disableCloseOnOutsideClick: boolean = false
    ): DialogOpener {
        const eopModalDialog = new EopModalDialog(dialogContent, dictionary, disableCloseOnOutsideClick);
        const dialogElement = this.pageFeatures.insert(eopModalDialog);
        return new DialogOpener(dialogElement, dialogContent);
    }
}
